import filter from 'lodash/filter';
import findIndex from 'lodash/findIndex';
import extend from 'lodash/extend';
import last from 'lodash/last';
import omit from 'lodash/omit';
import sortBy from 'lodash/sortBy';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import map from 'lodash/map';
import _remove from 'lodash/remove';

import { OnboardingServiceModule } from 'services/data/onboarding.service';

import { MenuServiceModule } from './menus.service';
import { ProductServiceModule } from './products.service';
import { DataServiceModule } from './data.service';

export const CategoryServiceModule = angular
    .module('app.services.data.categoryService', [
        ProductServiceModule,
        OnboardingServiceModule,
        DataServiceModule,
        MenuServiceModule
    ]).service('categoryService', (
        $q,
        $window,
        productService,
        menuService,
        dataService,
        onboardingService,
        settings
    ) => {
        let categoryTree;

        return {
            add,
            copy,
            createNewCategory,
            remove,
            update,
            updateAll,
            getCategories,
            getCategoryById,
            getCategoryTree,
            getFlatCategories,
            getCategory,
            getCategoryProducts,
            getMenuCount,
            saveCategoryProducts,
            flattenCategories,
            removeById,
            removeFieldsForCreate,
            updateCategory,
            updateCategoryProducts,
            updateCategoryImages,

            // the following are used for testing
            _setTree,
            _getTree
        };

        function _setTree(tree) {
            categoryTree = tree;
            return categoryTree;
        }

        function _getTree() {
            return categoryTree;
        }

        function getCategories(forceFetch) {
            // returns an array of categories
            if (categoryTree && !forceFetch) {
                return $q.resolve(angular.copy(categoryTree.categories));
            }

            return dataService.getAll(settings.api.categories, { pageSize: 200 })
                .then(updateCategoryTree)
                .then(tree => angular.copy(tree.categories));
        }

        function getCategoryTree() {
            return dataService.getAll(settings.api.adminCategories);
        }

        function getCategoryById(categoryId) {
            return dataService.get(settings.api.adminCategories, categoryId);
        }

        function getFlatCategories() {
            // returns a flat array of all categories (including subcategories)
            return getCategories().then(flattenCategories);
        }

        function findById(id) {
            return find(flattenCategories(categoryTree.categories), ['id', id]);
        }

        function flattenCategories(categories, shouldAddIndentationIndex = false, level = 0) {
            return categories.reduce((arr, category) => {
                return arr.concat(
                    [shouldAddIndentationIndex ? { ...category, level } : category],
                    flattenCategories(category.subCategories || [], shouldAddIndentationIndex, level + 1)
                );
            }, []);
        }

        function add({ category, parentId, showCreatedMessage = true, markTaskComplete = true }) {
            function addCategory() {
                const parent = findById(parentId || category.parentId);
                const categoryArray = parent ? parent.subCategories : categoryTree.categories;

                categoryArray.push(category);

                return save(categoryTree).then((data) => {
                    let taskId = 'organize.addCategory';
                    let firstCategoryCreatedKey = 'categoriesService:firstCategoryCreated';
                    if (showCreatedMessage) $window.sessionStorage.setItem('categoriesService:categoryCreated', true);

                    if (markTaskComplete) {
                        onboardingService.completeInitialTask(taskId, firstCategoryCreatedKey);
                    }

                    return data;
                }).then(returnLatestCategory);
            }

            return categoryTree
                ? addCategory()
                : getCategories().then(addCategory);
        }

        function remove(category) {
            let parent = findById(category.parentId);

            if (parent) {
                parent.subCategories = filter(parent.subCategories, el => el.id !== category.id);
            } else {
                categoryTree.categories = filter(categoryTree.categories, el => el.id !== category.id);
            }

            return save(categoryTree);
        }

        function removeById(categoryId) {
            return dataService.remove(settings.api.adminCategories, categoryId);
        }

        function update(category) {
            // returns updated category
            const parent = findById(category.parentId);
            const categoryArray = parent ? parent.subCategories : categoryTree.categories;

            const i = findIndex(categoryArray, ['id', category.id]);
            categoryArray.splice(i, 1, category);

            return save(categoryTree)
                .then(() => getCategory(category.id));
        }

        function removeFieldsForCreate(category) {
            return omit(category,
                '_id',
                'categoryId',
                'contentPageId',
                'createdOn',
                'id',
                'images',
                'productCount',
                'subCategories',
                'updatedOn'
            );
        }

        function createNewCategory(category) {
            return dataService.create(settings.api.adminCategories, category);
        }

        function updateCategory(category) {
            return dataService.save(settings.api.adminCategories, category.categoryId, category);
        }

        function copy(category, nameLength) {
            // returns copied category
            nameLength = nameLength || 35;
            var strLength = nameLength - 7;

            var copyItem = extend({}, category, {
                id: null,
                name: `${category.name.substring(0, strLength)} - COPY`,
                subCategories: []
            });

            const parent = findById(category.parentId);
            const categoryArray = parent ? parent.subCategories : categoryTree.categories;

            categoryArray.push(copyItem);

            return save(categoryTree)
                .then(returnLatestCategory);
        }

        function findMostRecent(categories) {
            let flatCategories = flattenCategories(categories);
            return last(sortBy(flatCategories, 'createdOn'));
        }

        function getCategory(id) {
            return categoryTree
                ? $q.resolve(findById(id))
                : getCategories().then(() => findById(id));
        }

        function getCategoryProducts(categoryId, page, count, sort) {
            var options = {
                filter: `categoryId eq ${categoryId}`
            };

            return productService.getMinimalProducts(page, count, sort, options);
        }

        function updateAll(categories) {
            categoryTree.categories = categories;

            return save(categoryTree);
        }

        function save(catTree) {
            return dataService.saveAll(settings.api.categories, catTree)
                .then(updateCategoryTree)
                .then(cTree => cTree.categories);
        }

        function updateCategoryTree(response) {
            categoryTree = response;
            return categoryTree;
        }

        function returnLatestCategory() {
            return findMostRecent(categoryTree.categories);
        }

        function getMenuCount(category) {
            return menuService.getMenus().then((response) => {
                const menus = response.items;
                const menuCount = reduce(menus, (count, menu) => {
                    const menuHasCategory = find(menu.items, { name: category.name });
                    return menuHasCategory ? count + 1 : count;
                }, 0);

                return menuCount;
            });
        }

        function updateCategoryProducts(category, productChanges) {
            if (productChanges) {
                const updateCategoryProductsUrl = `${settings.api.adminCategories}/${category.categoryId}/products`;
                return dataService.saveAll(updateCategoryProductsUrl, productChanges);
            }
        }

        function updateCategoryImages(category, imagesChanges) {
            if (imagesChanges) {
                const updateCategoryImagesUrl = `${settings.api.adminCategories}/${category.categoryId}/images`;
                return dataService.saveAll(updateCategoryImagesUrl, imagesChanges);
            }
        }

        function saveCategoryProducts(category, products) {
            let productPromises = [];

            if (products.length) {
                productPromises = map(products, (product) => {
                    if (product.categoryIds.indexOf(category.id) !== -1) {
                        _remove(product.categoryIds, id => id === category.id);
                    } else {
                        product.categoryIds.push(category.id);
                    }

                    return productService.save(product);
                });

                return $q.all(productPromises).then(() => category);
            }

            return $q.resolve(category);
        }
    }).name;



// WEBPACK FOOTER //
// ./src/app/services/data/categories.service.js